/**
 * Ícones da manutenção — traço simples, em SVG, sem dependência nova. Servem às seções do
 * checklist (Motor, Freios…) e aos cartões da execução técnica (óleos, filtros, rolamento,
 * cubo…). O modelo de checklist guarda só o NOME do ícone (`icon`), nunca a imagem.
 */
import type { ReactNode } from "react";

const P = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;

const ICONS: Record<string, ReactNode> = {
  engine: (<g {...P}><rect x="5" y="9" width="12" height="9" rx="1.5" /><path d="M8 9V6h6v3M17 12h3v4h-3M5 13H3M9 18v2M13 18v2" /><circle cx="11" cy="13.5" r="1.8" /></g>),
  gearbox: (<g {...P}><rect x="3" y="7" width="14" height="10" rx="2" /><path d="M17 10h4v4h-4M7 7V4h6v3M6 11h8M6 14h8" /></g>),
  differential: (<g {...P}><path d="M2 12h20" /><circle cx="12" cy="12" r="4.5" /><path d="M12 7.5V4M12 20v-3.5" /><circle cx="12" cy="12" r="1.2" /></g>),
  "filter-oil": (<g {...P}><rect x="7" y="6" width="10" height="13" rx="1.5" /><path d="M9 6V4h6v2M7 10h10M7 15h10" /></g>),
  "filter-air": (<g {...P}><rect x="6" y="5" width="12" height="14" rx="2" /><path d="M9 8v8M12 8v8M15 8v8" /></g>),
  "filter-fuel": (<g {...P}><rect x="8" y="8" width="8" height="11" rx="1.5" /><path d="M10 8V5h4v3M12 3v2M8 12h8" /></g>),
  "filter-water": (<g {...P}><path d="M9 5h6M10 5v3M14 5v3" /><path d="M8 8h8l-.8 9a2 2 0 0 1-2 1.8h-2.4a2 2 0 0 1-2-1.8z" /><path d="M9.5 13c1 .8 2 .8 3 0s1.5-.8 2 0" /></g>),
  "filter-adblue": (<g {...P}><rect x="7" y="7" width="10" height="12" rx="1.5" /><path d="M9 7V5h6v2M12 10c-1.5 2-2 3-2 4a2 2 0 0 0 4 0c0-1-.5-2-2-4z" /></g>),
  bearing: (<g {...P}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 4v1.5M12 18.5V20M4 12h1.5M18.5 12H20M6.3 6.3l1 1M16.7 16.7l1 1M17.7 6.3l-1 1M7.3 16.7l-1 1" /></g>),
  hub: (<g {...P}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.5" /><circle cx="12" cy="6.5" r=".8" /><circle cx="12" cy="17.5" r=".8" /><circle cx="6.5" cy="12" r=".8" /><circle cx="17.5" cy="12" r=".8" /></g>),
  seal: (<g {...P}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></g>),
  brake: (<g {...P}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2" /><path d="M16.5 6.5a7 7 0 0 1 2 5M7.5 17.5a7 7 0 0 1-2-5" /></g>),
  grease: (<g {...P}><path d="M4 18l9-9M12 6l6 6M14 4l6 6-2.5 2.5-6-6z" /><path d="M4 20l2-2" /></g>),
  axle: (<g {...P}><path d="M3 12h18" /><rect x="3" y="7" width="3" height="10" rx="1" /><rect x="18" y="7" width="3" height="10" rx="1" /><circle cx="12" cy="12" r="2" /></g>),
  wrench: (<g {...P}><path d="M14.5 5.5a4 4 0 0 0-5 5L4 16l4 4 5.5-5.5a4 4 0 0 0 5-5L16 12l-3-1-1-3z" /></g>),
  tire: (<g {...P}><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" /></g>),
  cooling: (<g {...P}><rect x="5" y="4" width="14" height="14" rx="1.5" /><path d="M8 4v14M12 4v14M16 4v14M9 21h6" /></g>),
  suspension: (<g {...P}><path d="M8 4h8M8 4c0 2 8 2 8 4s-8 2-8 4 8 2 8 4M8 18h8M12 18v3" /></g>),
  light: (<g {...P}><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /></g>),
  generic: (<g {...P}><rect x="5" y="5" width="14" height="14" rx="3" /><path d="M9 12l2 2 4-4" /></g>),
};

export type MaintenanceIconName = keyof typeof ICONS | (string & {});

export function MaintenanceIcon({ name, className = "h-5 w-5" }: { name?: string | null; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      {ICONS[name ?? "generic"] ?? ICONS.generic}
    </svg>
  );
}

/** Ícone da seção pelo título (Motor, Freios, Pneus…) — só apresentação, nada depende disso. */
export function sectionIconName(title: string): string {
  const t = title.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  if (/arrefec|radiador|refriger/.test(t)) return "cooling";
  if (/motor/.test(t)) return "engine";
  if (/transmiss|cambio|diferencial/.test(t)) return "gearbox";
  if (/freio/.test(t)) return "brake";
  if (/chassi|suspens|eixo/.test(t)) return "suspension";
  if (/pneu|roda/.test(t)) return "tire";
  if (/luz|ilumin|eletric/.test(t)) return "light";
  return "wrench";
}
